import axiosInstance from "../helpers/axiosInstance"

export default class S3Client {
  constructor({ 
    newMultipartUploadPath,
    generateMultipartUploadPresignedUrlsPath,
    completeMultipartUploadPath,
    listFilesWithPrefixPath,
   })
  {
    this.resetMultiPartUpload()
    this.newMultipartUploadPath = newMultipartUploadPath
    this.generateMultipartUploadPresignedUrlsPath = generateMultipartUploadPresignedUrlsPath
    this.completeMultipartUploadPath = completeMultipartUploadPath
    this.listFilesWithPrefixPath = listFilesWithPrefixPath
  }

  resetMultiPartUpload() {
    this.currentMultipartUpload = {
      object: null,
      uploadId: null,
      parts: [],
      uploadedParts: [],
      fileKey: null,
      presignedUploadUrls: [],
    }
  }

  async createMultipartUpload(fileKey, presignedUploadUrls = 5) {
    
    const body = {
      filename: fileKey,
      presign_parts: presignedUploadUrls,
    }
    const response = await axiosInstance.post(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/packing-proof/new-multipart-upload`, body )
    const responseData = response.data
    
    this.currentMultipartUpload.uploadId = responseData.upload_id
    this.currentMultipartUpload.fileKey = fileKey
    this.currentMultipartUpload.presignedUploadUrls = responseData.presigned_urls
    return true
  }

  async uploadPart(part, partIndex) {
    if (!this.currentMultipartUpload.uploadId) throw new Error('Must initialize multipart upload first')
    this.currentMultipartUpload.parts[partIndex] = part

    let presignedUrl = this.currentMultipartUpload.presignedUploadUrls[partIndex + 1]

    if (!presignedUrl) {
      console.log(`No presigned URL available for part ${partIndex + 1}, generating new one...`)
      const newPresignedUrlsResponse = await this.generatePresignedUrlsForUpload([partIndex + 1])
      // update the presignedUrl
      presignedUrl = newPresignedUrlsResponse[partIndex + 1]
    }
    
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      body: part,
      headers: {
        'Content-Type': 'application/octet-stream'
      }
    })

    const eTag = response.headers.get('ETag')?.replace(/"/g, '');
    const responseUploadPart = {
      ETag: eTag,
      PartNumber: partIndex + 1
    }
    
    this.currentMultipartUpload.uploadedParts[partIndex] = responseUploadPart
    return responseUploadPart
  }

  allPartsUploaded() {
    if (!this.currentMultipartUpload?.parts?.length) return false
    return this.currentMultipartUpload.parts.length === this.currentMultipartUpload.uploadedParts?.length
  }

  async completeMultipartUpload() {
    if (this.currentMultipartUpload.parts.length === 0) throw new Error('No parts to complete')
    if (!this.currentMultipartUpload.uploadId) throw new Error('No multipart update to complete')
    if (!this.allPartsUploaded()) throw new Error('Not all parts have been uploaded')

    const completedParts = this.currentMultipartUpload.uploadedParts.map((part, index) => ({
      etag: part.ETag,
      part_number: index + 1,
    }))

    // throw error if any uploaded parts have no etag
    if (completedParts.some((part) => !part.etag)) throw new Error('Not all uploaded parts have an ETag')

    console.log('Completing multipart upload for', this.currentMultipartUpload.fileKey)

    const body = {
      upload_id: this.currentMultipartUpload.uploadId,
      key: this.currentMultipartUpload.fileKey,
      parts: completedParts,
    }

    const response = await axiosInstance.post(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/packing-proof/complete-upload`, body )

    this.resetMultiPartUpload()
    console.log('Multipart upload completed successfully', response)
    return response
  }

  async listFilesWithPrefix(prefix) {
    const params = {
      prefix: prefix,
    }
    const response = await axiosInstance.get(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/packing-proof/list`, { params } )
    // console.log('List files with prefix response:', response.data)
    const objects = []
    if (response.data) {
      response.data.forEach(obj => {
      objects.push(obj)
      })
    }
    return objects
  }

  async generatePresignedUrlsForUpload(partNumbers) {
    if (!this.currentMultipartUpload.uploadId) throw new Error('Must initialize multipart upload first')
    if (!partNumbers || !Array.isArray(partNumbers) || partNumbers.length === 0) {
      throw new Error('Must provide an array of part numbers to generate presigned URLs')
    }
    const body = {
      upload_id: this.currentMultipartUpload.uploadId,
      part_numbers: partNumbers,
      key: this.currentMultipartUpload.fileKey,
    }
      
    const response = await axiosInstance.post(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/packing-proof/upload-presigned-urls`, body )

    return response.data?.presigned_urls
  }

}
